/**
 * Browser diagnostic collector + transport (OBS-VALIDATION-01 §5/§7).
 * Does not modify capture graph. Queue bounded; teardown wait added = 0.
 */
import type { Room } from 'livekit-client';
import { api } from '../../api';
import { type DiagEnvelope, type DiagMode, makeEnvelope, newSourceInstanceId } from './envelope';

const MAX_QUEUE_BYTES = 256 * 1024;
const MAX_PAYLOAD_BYTES = 8 * 1024;

export type DiagActivation = {
  diagnostic_run_id: string;
  mode: DiagMode;
  expiry_unix: number | null;
  input_kind: 'real_microphone' | 'cue_destination' | 'other' | 'unknown';
  config_source: string;
};

function parseMode(v: unknown): DiagMode {
  const s = String(v || '').toLowerCase();
  if (s === 'summary' || s === 'trace' || s === 'off') return s;
  return 'off';
}

export function resolveBrowserActivation(sessionMeta: Record<string, unknown> | null | undefined): DiagActivation {
  const diag = (sessionMeta?.diagnostics || sessionMeta?.diagnostic || {}) as Record<string, unknown>;
  let mode: DiagMode = 'off';
  let runId = '';
  let expiry: number | null = null;
  let source = 'default_off';
  let inputKind: DiagActivation['input_kind'] = 'unknown';
  if (diag && typeof diag === 'object' && Object.keys(diag).length > 0) {
    source = 'session_meta';
    if (diag.enabled === false) mode = 'off';
    else mode = parseMode(diag.mode || (diag.enabled ? 'summary' : 'off'));
    runId = String(diag.run_id || diag.diagnostic_run_id || '');
    if (diag.expires_at != null) {
      const n = Number(diag.expires_at);
      expiry = Number.isFinite(n) ? n : null;
    }
    if (diag.input_kind) inputKind = String(diag.input_kind) as DiagActivation['input_kind'];
  }
  // Build-time / URL opt-in (OBS-VALIDATION-01 activation without redeploying session create).
  if (source === 'default_off' && typeof import.meta !== 'undefined') {
    const viteMode = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PULSE_DIAG_MODE;
    if (viteMode) {
      source = 'vite_env';
      mode = parseMode(viteMode);
      runId = String(
        (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PULSE_DIAG_RUN_ID || '',
      );
      const exp = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PULSE_DIAG_EXPIRES_UNIX;
      if (exp) {
        const n = Number(exp);
        expiry = Number.isFinite(n) ? n : null;
      }
      const ik = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_PULSE_DIAG_INPUT_KIND;
      if (ik) inputKind = ik as DiagActivation['input_kind'];
    }
  }
  if (typeof window !== 'undefined') {
    try {
      const q = new URLSearchParams(window.location.search);
      const qMode = q.get('pulseDiag') || q.get('diag');
      if (qMode) {
        source = 'url_query';
        mode = parseMode(qMode);
        runId = q.get('diagRunId') || q.get('pulseDiagRunId') || runId;
        const exp = q.get('diagExpires');
        if (exp) {
          const n = Number(exp);
          expiry = Number.isFinite(n) ? n : null;
        }
        const ik = q.get('diagInputKind');
        if (ik) inputKind = ik as DiagActivation['input_kind'];
      }
    } catch {
      /* ignore */
    }
  }
  if (!runId && mode !== 'off') runId = `diag-${Date.now()}`;
  if (!runId) runId = 'diag-off';
  if (expiry != null && Date.now() / 1000 > expiry) mode = 'off';
  return {
    diagnostic_run_id: runId,
    mode,
    expiry_unix: expiry,
    input_kind: inputKind,
    config_source: source,
  };
}

export class BrowserDiagCollector {
  readonly source_instance_id = newSourceInstanceId('browser');
  private sourceSeq = 0;
  private sessionSeq = 0;
  private queue: DiagEnvelope[] = [];
  private bytes = 0;
  private drops: Record<string, number> = {};
  activation: DiagActivation;
  sessionId: string | null;
  roomName: string | null;
  buildRef: string;

  constructor(args: {
    activation: DiagActivation;
    sessionId: string | null;
    roomName?: string | null;
    buildRef?: string;
  }) {
    this.activation = args.activation;
    this.sessionId = args.sessionId;
    this.roomName = args.roomName ?? null;
    this.buildRef = args.buildRef || 'conversation-bundle-unknown';
  }

  get active(): boolean {
    if (this.activation.mode === 'off') return false;
    if (this.activation.expiry_unix != null && Date.now() / 1000 > this.activation.expiry_unix) {
      return false;
    }
    return true;
  }

  emit(
    event: string,
    data: Record<string, unknown> = {},
    opts: {
      unavailable?: Record<string, string>;
      provenance?: string;
      force?: boolean;
      parent_event_id?: string;
      operation_id?: string;
    } = {},
  ): DiagEnvelope | null {
    if (!opts.force && !this.active) return null;
    this.sourceSeq += 1;
    let sessionSeq: number | null = null;
    if (this.sessionId) {
      this.sessionSeq += 1;
      sessionSeq = this.sessionSeq;
    }
    const env = makeEnvelope({
      event,
      source_instance_id: this.source_instance_id,
      source_seq: this.sourceSeq,
      session_seq: sessionSeq,
      diagnostic_run_id: this.activation.diagnostic_run_id,
      session_id: this.sessionId,
      room_name: this.roomName,
      component: 'browser',
      build_ref: this.buildRef,
      mode: this.activation.mode,
      data,
      unavailable: opts.unavailable,
      provenance: opts.provenance,
      parent_event_id: opts.parent_event_id,
      operation_id: opts.operation_id,
    });
    const encoded = JSON.stringify(env);
    if (encoded.length > MAX_PAYLOAD_BYTES) {
      this.drops.payload_too_large = (this.drops.payload_too_large || 0) + 1;
      // Split not implemented for oversized single events — keep mandatory fields only.
      env.data = { truncated: true, original_event: event };
    }
    const size = encoded.length;
    while (this.bytes + size > MAX_QUEUE_BYTES && this.queue.length) {
      const dropped = this.queue.shift();
      if (dropped) this.bytes -= JSON.stringify(dropped).length;
      this.drops.queue_overflow = (this.drops.queue_overflow || 0) + 1;
    }
    this.queue.push(env);
    this.bytes += size;
    return env;
  }

  /** Best-effort dispatch; never awaited on hangup teardown. */
  dispatch(room: Room | null, sessionId: string): void {
    const batch = this.queue.splice(0, this.queue.length);
    this.bytes = 0;
    for (const env of batch) {
      const payload = { type: env.event, ...env };
      try {
        const bytes = new TextEncoder().encode(JSON.stringify(payload));
        void room?.localParticipant.publishData(bytes, { reliable: false });
      } catch {
        this.drops.data_channel_fail = (this.drops.data_channel_fail || 0) + 1;
      }
      // HTTP allowlisted subset only (control-api).
      const short = env.event.replace(/^diag\.client\./, '').replace(/^client\./, '');
      const httpOk = new Set([
        'stats_sample',
        'end_flow',
        'track_state',
        'context_state',
        'source_registered',
        'webrtc_stats',
        'visibility',
        'audio_context',
        'track_muted',
        'track_unmuted',
      ]);
      if (httpOk.has(short) || env.event.startsWith('diag.client.')) {
        const isStats =
          short === 'stats_sample' || env.event === 'diag.client.stats_sample';
        // Slim HTTP meta for stats_sample — full envelope still goes on data channel.
        // Control-api drops stats_sample list_append entirely; keep POST tiny.
        const meta: Record<string, unknown> = isStats
          ? {
              session_id: env.session_id,
              connection_quality: (env.data as { connection_quality?: unknown } | undefined)
                ?.connection_quality,
              mode: env.mode,
              diagnostic_run_id: env.diagnostic_run_id,
            }
          : (env as unknown as Record<string, unknown>);
        void api
          .reportClientEvent(sessionId, {
            type: env.event.startsWith('diag.') ? env.event : short,
            meta,
          })
          .catch(() => undefined);
      }
    }
  }

  health() {
    return { drops: { ...this.drops }, queued: this.queue.length, bytes: this.bytes };
  }
}
